import type { OptimizedImage, Ring } from "../types";
import type { AttributeOption } from "./ring-attributes";

const DEFAULT_STORAGE_BUCKET = "ring-swipe.appspot.com";

/**
 * Convert an array of attribute options to an object with value as key
 */
export function getValuesAsObject(options: AttributeOption[]): Record<string, boolean> {
  const result: Record<string, boolean> = {};
  for (const option of options) {
    result[option.value] = option.defaultValue ?? true;
  }
  return result;
}

/**
 * Find the label for a given value in an attribute options array
 */
export function getLabelByValue(options: AttributeOption[], value: string): string | null {
  const found = options.find((item) => item.value === value);
  return found ? found.label : null;
}

/**
 * Filter options that match values in an array or string
 */
export function getMatchingOptions(
  options: AttributeOption[],
  values: string | string[]
): AttributeOption[] {
  const valueArray = Array.isArray(values) ? values : [values];
  return options.filter((option) => valueArray.includes(option.value));
}

/**
 * Get all values from an attribute options array
 */
export function getValues(options: AttributeOption[]): string[] {
  return options.map((option) => option.value);
}

/**
 * Normalize stoneCut field which can be string or array
 */
export function normalizeStoneCut(stoneCut: string | string[] | undefined): string[] {
  if (!stoneCut) return [];
  return Array.isArray(stoneCut) ? stoneCut : [stoneCut];
}

/**
 * Normalize url field which can be string or array
 */
export function normalizeUrl(url: string | string[] | undefined): string[] {
  if (!url) return [];
  return Array.isArray(url) ? url : [url];
}

function normalizeStoragePath(value: string | undefined): string {
  if (!value) return "";
  try {
    const stripped = value.split("#")[0];
    if (!/^https?:\/\//i.test(stripped) && !stripped.startsWith("gs://")) {
      return decodeURIComponent(stripped.split("?")[0]);
    }

    if (stripped.startsWith("gs://")) {
      const withoutScheme = stripped.slice(5);
      const slashIndex = withoutScheme.indexOf("/");
      return slashIndex === -1 ? "" : decodeURIComponent(withoutScheme.slice(slashIndex + 1));
    }

    const parsed = new URL(stripped);

    if (parsed.hostname === "storage.googleapis.com") {
      const parts = parsed.pathname.split("/").filter(Boolean);
      if (parts.length < 2) return "";
      return decodeURIComponent(parts.slice(1).join("/")).split("?")[0];
    }

    if (parsed.hostname === "firebasestorage.googleapis.com") {
      const parts = parsed.pathname.split("/").filter(Boolean);
      const objectIndex = parts.indexOf("o");
      if (objectIndex === -1) return "";
      return decodeURIComponent(parts[objectIndex + 1] || "").split("?")[0];
    }
  } catch {
    return "";
  }

  return "";
}

function getSignedVariantUrl(
  entry: OptimizedImage | undefined,
  preferredWidths: string[],
): string | null {
  if (!entry) return null;

  for (const width of preferredWidths) {
    const value = entry.variants?.[width];
    if (typeof value === "string" && /^https?:\/\//i.test(value)) {
      return value;
    }
  }

  return null;
}

function toFirebaseMediaUrl(path: string): string {
  return `https://firebasestorage.googleapis.com/v0/b/${DEFAULT_STORAGE_BUCKET}/o/${encodeURIComponent(path)}?alt=media`;
}

function getVariantUrl(
  entry: OptimizedImage | undefined,
  preferredWidths: string[],
): string | null {
  const signedUrl = getSignedVariantUrl(entry, preferredWidths);
  if (signedUrl) return signedUrl;
  if (!entry) return null;

  for (const width of preferredWidths) {
    const value = entry.variants?.[width];
    if (typeof value === "string" && value) {
      return /^https?:\/\//i.test(value) ? value : toFirebaseMediaUrl(value);
    }
  }

  return null;
}

/**
 * Prefer signed optimized image variants when present, otherwise fall back to ring.url.
 */
export function getRingImageUrls(
  ring: Pick<Ring, "url" | "optimizedImages">,
  preferredWidths: string[] = ["800", "1600", "400"],
): string[] {
  const urls = normalizeUrl(ring.url);
  if (!ring.optimizedImages?.length) return urls;

  const optimizedMap = new Map<string, OptimizedImage>();
  for (const entry of ring.optimizedImages) {
    if (entry?.original) {
      optimizedMap.set(normalizeStoragePath(entry.original), entry);
    }
  }

  return urls.map((url) => {
    const optimized = getVariantUrl(optimizedMap.get(normalizeStoragePath(url)), preferredWidths);
    return optimized || url;
  });
}

/**
 * Format price with currency symbol
 */
export function formatPrice(price: number | undefined, currency: string = "USD"): string {
  if (price === undefined || price === null) return "";
  const symbol = currency === "EUR" ? "€" : "$";
  return `${symbol}${price.toLocaleString()}`;
}

/**
 * Generate a URL-friendly slug from a ring name
 */
export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80);
}
